import { normalizeOptionalArPhone } from '@shared/phone';
import { supabase } from '../../shared/supabase/supabaseClient';
import { customerPortalBaseUrl } from '../../shared/customerPortalUrl';
import { captureOpsFailure } from '../../shared/sentry/sentryOps';
import { getCachedStoreProfile } from '../../settings/application/storefrontStatus';
import { storefrontPublicUrl, type BusinessHourDay } from '../../settings/domain/storeProfileTypes';
import type {
  Appointment,
  AppointmentDepositResolutionStatus,
  AppointmentItem,
  AppointmentResource,
  AppointmentStatus,
  AppointmentWaitlistEntry,
  BookingPaymentMode,
  BookingSettings,
  ResourceHour,
  ResourceTimeOff,
  ServiceProductOption,
} from '../domain/types';
import { appointmentItemsToRpcPayload } from '../domain/appointmentItems';
import {
  fireAppointmentStaffNotify,
  resolveStaffNotifyAfterStatus,
  resolveStaffNotifyAfterUpdate,
} from './appointmentStaffNotify';

async function fireAppointmentEmail(
  appointmentId: string,
  kind: 'confirmation' | 'cancellation',
): Promise<void> {
  try {
    const { error } = await supabase.functions.invoke('send-appointment-email', {
      body: { appointment_id: appointmentId, kind },
    });
    if (error) {
      console.warn('[appointmentsRepo] send-appointment-email failed', error.message, kind);
    }
  } catch (e) {
    console.warn('[appointmentsRepo] send-appointment-email error', e);
  }
}

type ResourceRow = {
  id: string;
  organization_id: string;
  branch_id: string;
  display_name: string;
  employee_id: string | null;
  user_id: string | null;
  color: string | null;
  is_active: boolean;
  sort_order: number;
  resource_kind?: string | null;
  appointment_resource_hours?: Array<{
    day_of_week: number;
    start_time: string;
    end_time: string;
  }> | null;
};

type AppointmentRow = {
  id: string;
  organization_id: string;
  branch_id: string;
  resource_id: string;
  product_id: string;
  customer_id?: string | null;
  customer_name: string;
  customer_phone: string | null;
  customer_email: string | null;
  starts_at: string;
  ends_at: string;
  status: AppointmentStatus;
  source: 'portal' | 'app';
  notes: string | null;
  payment_mode: BookingPaymentMode;
  payment_status: string;
  amount_total: number | null;
  amount_paid?: number | null;
  sale_id?: string | null;
  arrived_at?: string | null;
  quote_request_id?: string | null;
  session_index?: number | null;
  package_entitlement_id?: string | null;
  deposit_resolution_status?: string | null;
  deposit_resolution_amount?: number | null;
  resource_name?: { display_name: string }[] | string | null;
};

const APPOINTMENT_SELECT =
  'id, organization_id, branch_id, resource_id, product_id, customer_id, customer_name, customer_phone, customer_email, starts_at, ends_at, status, source, notes, payment_mode, payment_status, amount_total, amount_paid, sale_id, arrived_at, quote_request_id, session_index, package_entitlement_id, deposit_resolution_status, deposit_resolution_amount, resource_name:appointment_resources(display_name)';

function mapHours(rows: ResourceRow['appointment_resource_hours']): ResourceHour[] {
  return (rows ?? []).map((h) => ({
    dayOfWeek: Number(h.day_of_week),
    startTime: String(h.start_time).slice(0, 5),
    endTime: String(h.end_time).slice(0, 5),
  }));
}

function mapResource(row: ResourceRow): AppointmentResource {
  const kind = row.resource_kind;
  return {
    id: row.id,
    organizationId: row.organization_id,
    branchId: row.branch_id,
    displayName: row.display_name,
    employeeId: row.employee_id ?? null,
    userId: row.user_id,
    color: row.color,
    isActive: row.is_active,
    sortOrder: row.sort_order,
    resourceKind:
      kind === 'bay' || kind === 'equipment' || kind === 'person' ? kind : 'person',
    hours: mapHours(row.appointment_resource_hours),
  };
}

function mapAppointment(row: AppointmentRow, items?: AppointmentItem[] | undefined): Appointment {
  return {
    id: row.id,
    organizationId: row.organization_id,
    branchId: row.branch_id,
    resourceId: row.resource_id,
    productId: row.product_id,
    items,
    customerId: row.customer_id ?? null,
    customerName: row.customer_name,
    customerPhone: row.customer_phone,
    customerEmail: row.customer_email,
    startsAt: row.starts_at,
    endsAt: row.ends_at,
    status: row.status,
    source: row.source,
    notes: row.notes,
    paymentMode: row.payment_mode,
    paymentStatus: row.payment_status,
    amountTotal: row.amount_total != null ? Number(row.amount_total) : null,
    amountPaid: row.amount_paid != null ? Number(row.amount_paid) : null,
    saleId: row.sale_id ?? null,
    arrivedAt: row.arrived_at ?? null,
    quoteRequestId: row.quote_request_id ?? null,
    sessionIndex: row.session_index != null ? Number(row.session_index) : null,
    packageEntitlementId: row.package_entitlement_id ?? null,
    depositResolutionStatus:
      (row.deposit_resolution_status ?? 'not_applicable') as AppointmentDepositResolutionStatus,
    depositResolutionAmount: row.deposit_resolution_amount != null ? Number(row.deposit_resolution_amount) : null,
    resourceName: (Array.isArray(row.resource_name) && row.resource_name.length > 0 ? row.resource_name[0]!.display_name : (typeof row.resource_name === 'string' ? row.resource_name : null)) ?? null,
  };
}

function mapAppointmentItemRow(row: {
  id?: string;
  product_id: string;
  sort_order: number;
  name_snapshot: string;
  price_snapshot: number;
  duration_minutes: number;
}): AppointmentItem {
  return {
    id: row.id,
    productId: row.product_id,
    sortOrder: Number(row.sort_order) || 0,
    nameSnapshot: row.name_snapshot,
    priceSnapshot: Number(row.price_snapshot) || 0,
    durationMinutes: Number(row.duration_minutes) || 0,
  };
}

export async function listAppointmentItems(appointmentId: string): Promise<AppointmentItem[]> {
  const { data, error } = await supabase.rpc('list_appointment_items', {
    p_appointment_id: appointmentId,
  });
  if (error) {
    // Fallback: direct table read (RLS)
    const { data: rows, error: qErr } = await supabase
      .from('appointment_items')
      .select('id, product_id, sort_order, name_snapshot, price_snapshot, duration_minutes')
      .eq('appointment_id', appointmentId)
      .order('sort_order', { ascending: true });
    if (qErr) throw new Error(error.message || qErr.message);
    return ((rows ?? []) as Array<{
      id: string;
      product_id: string;
      sort_order: number;
      name_snapshot: string;
      price_snapshot: number;
      duration_minutes: number;
    }>).map(mapAppointmentItemRow);
  }
  const raw = (data ?? []) as Array<{
    id?: string;
    product_id: string;
    sort_order: number;
    name_snapshot: string;
    price_snapshot: number;
    duration_minutes: number;
  }>;
  return raw.map(mapAppointmentItemRow);
}

async function attachItems(appointment: Appointment): Promise<Appointment> {
  try {
    const items = await listAppointmentItems(appointment.id);
    if (items.length === 0) return appointment;
    return { ...appointment, items };
  } catch {
    return appointment;
  }
}

/** Postgres exclusion / overlap → mensaje usable en UI. */
export function appointmentWriteErrorMessage(message: string, fallback: string): string {
  if (isAppointmentOverlapMessage(message)) {
    return 'Ese horario ya está ocupado. Elegí otro.';
  }
  return message.trim() || fallback;
}

function isAppointmentOverlapMessage(message: string): boolean {
  return (
    message.includes('appointments_resource_no_overlap') ||
    message.includes('conflicting key value violates exclusion constraint')
  );
}

function throwAppointmentWriteFailure(
  op: string,
  error: unknown,
  itemId?: string,
  expected = false,
): never {
  const err = error instanceof Error ? error : new Error(String(error));
  captureOpsFailure({
    module: 'appointments',
    op,
    error: err,
    expected,
    ingestError: err.message,
    itemId,
    extra: itemId ? { appointmentId: itemId } : undefined,
  });
  throw err;
}

export async function listResources(branchId: string): Promise<AppointmentResource[]> {
  const { data, error } = await supabase
    .from('appointment_resources')
    .select(
      'id, organization_id, branch_id, display_name, employee_id, user_id, color, is_active, sort_order, resource_kind, appointment_resource_hours(day_of_week, start_time, end_time)',
    )
    .eq('branch_id', branchId)
    .order('sort_order', { ascending: true })
    .order('display_name', { ascending: true });

  if (error) throw new Error(error.message);
  return ((data ?? []) as ResourceRow[]).map(mapResource);
}

export function buildResourceHoursRpcPayload(hours: ResourceHour[]) {
  return hours.map((h) => ({
    dayOfWeek: h.dayOfWeek,
    startTime: h.startTime,
    endTime: h.endTime,
  }));
}

export async function createResource(input: {
  organizationId: string;
  branchId: string;
  displayName: string;
  employeeId?: string | null;
  resourceKind?: AppointmentResource['resourceKind'];
  /** If omitted, seeds from store business_hours or Lun–Vie 09–18. */
  hours?: ResourceHour[];
}): Promise<AppointmentResource> {
  const name = input.displayName.trim();
  if (!name) throw new Error('Nombre requerido');

  const hours = input.hours ?? (await resolveSeedHours(input.branchId));

  const kind = input.resourceKind ?? 'person';

  const { data: rpcResult, error } = await supabase.rpc('create_resource_with_hours', {
    p_organization_id: input.organizationId,
    p_branch_id: input.branchId,
    p_display_name: name,
    p_employee_id: input.employeeId ?? null,
    p_hours: buildResourceHoursRpcPayload(hours),
    p_resource_kind: kind,
  });

  if (error) throw new Error(error.message);

  const resourceId = (rpcResult as { resource_id?: string })?.resource_id;
  if (!resourceId) throw new Error('No se pudo crear la agenda.');

  // Fetch the created resource to return full row
  const { data: resource, error: fetchError } = await supabase
    .from('appointment_resources')
    .select(
      'id, organization_id, branch_id, display_name, employee_id, user_id, color, is_active, sort_order, resource_kind',
    )
    .eq('id', resourceId)
    .single();

  if (fetchError || !resource) throw new Error('No se pudo crear la agenda.');

  return mapResource({
    ...resource,
    appointment_resource_hours: hours.map((h) => ({
      day_of_week: h.dayOfWeek,
      start_time: h.startTime,
      end_time: h.endTime,
    })),
  });
}

export async function updateResource(
  id: string,
  patch: {
    displayName?: string;
    employeeId?: string | null;
    sortOrder?: number;
    color?: string | null;
    resourceKind?: AppointmentResource['resourceKind'];
  },
): Promise<void> {
  const update: Record<string, unknown> = { updated_at: new Date().toISOString() };
  if (patch.displayName != null) {
    const displayName = patch.displayName.trim();
    if (!displayName) throw new Error('Nombre requerido');
    if (displayName.length > 120) throw new Error('El nombre no puede superar los 120 caracteres');
    update.display_name = displayName;
  }
  if (patch.employeeId !== undefined) update.employee_id = patch.employeeId;
  if (patch.sortOrder != null) update.sort_order = patch.sortOrder;
  if (patch.color !== undefined) update.color = patch.color;
  if (patch.resourceKind != null) update.resource_kind = patch.resourceKind;

  const { error } = await supabase.from('appointment_resources').update(update).eq('id', id);
  if (error) throw new Error(error.message);
}

export type AgendaEmployeeOption = {
  id: string;
  authUserId: string;
  name: string;
  role: string;
  /** Membresía del dueño (role='owner' en employees): identidad, no empleo. */
  isOwner?: boolean;
};

type AgendaEmployeeRow = {
  id: string;
  auth_user_id: string;
  full_name: string | null;
  role: string | null;
  organization_id: string | null;
};

/**
 * Empleados: requieren permiso Agenda (can_view) en la sucursal.
 * Dueño (role='owner'): entra siempre, sin EBP — pero solo la row de esta org
 * (multi-org: su row owner de otra org no debe aparecer acá).
 */
export function buildAgendaEmployeeOptions(
  employees: AgendaEmployeeRow[],
  allowedEmployeeIds: ReadonlySet<string>,
  organizationId: string | null,
): AgendaEmployeeOption[] {
  return employees
    .filter((row) => {
      if (row.role === 'owner') {
        return organizationId != null && row.organization_id === organizationId;
      }
      return allowedEmployeeIds.has(row.id);
    })
    .map((row) => {
      const isOwner = row.role === 'owner';
      return {
        id: row.id,
        authUserId: row.auth_user_id,
        name: row.full_name?.trim() || (isOwner ? 'Vos' : row.auth_user_id.slice(0, 8)),
        role: isOwner ? 'Dueño' : row.role?.trim() || 'Empleado',
        isOwner,
      };
    });
}

export async function listAgendaEmployees(branchId: string): Promise<AgendaEmployeeOption[]> {
  const [
    { data: branch },
    { data: employees, error: employeesError },
    { data: permissions, error: permissionsError },
  ] = await Promise.all([
    supabase
      .from('branches')
      .select('organization_id')
      .eq('id', branchId)
      .maybeSingle(),
    supabase
      .from('employees')
      .select('id, auth_user_id, full_name, role, is_active, organization_id')
      .eq('is_active', true)
      .order('full_name', { ascending: true }),
    supabase
      .from('employee_branch_permissions')
      .select('employee_id')
      .eq('branch_id', branchId)
      .eq('module', 'Agenda')
      .eq('can_view', true)
      .is('revoked_at', null),
  ]);

  if (employeesError) throw new Error(employeesError.message);
  if (permissionsError) throw new Error(permissionsError.message);

  const orgId = (branch as { organization_id?: string | null } | null)?.organization_id ?? null;
  const allowedIds = new Set((permissions ?? []).map((row: { employee_id: string }) => row.employee_id));
  return buildAgendaEmployeeOptions((employees ?? []) as AgendaEmployeeRow[], allowedIds, orgId);
}

export async function linkResourceToEmployee(resourceId: string, employeeId: string | null): Promise<void> {
  const { error } = await supabase
    .from('appointment_resources')
    .update({ employee_id: employeeId, updated_at: new Date().toISOString() })
    .eq('id', resourceId);
  if (error) throw new Error(error.message);
}

export async function deactivateResource(id: string): Promise<void> {
  await setResourceActive(id, false);
}

export async function activateResource(id: string): Promise<void> {
  await setResourceActive(id, true);
}

async function setResourceActive(id: string, isActive: boolean): Promise<void> {
  const { error } = await supabase
    .from('appointment_resources')
    .update({ is_active: isActive, updated_at: new Date().toISOString() })
    .eq('id', id);
  if (error) throw new Error(error.message);
}

export async function setResourceHours(resourceId: string, hours: ResourceHour[]): Promise<void> {
  const hoursArray = hours.map((h) => ({
    dayOfWeek: h.dayOfWeek,
    startTime: h.startTime,
    endTime: h.endTime,
  }));

  const { error } = await supabase.rpc('set_resource_hours', {
    p_resource_id: resourceId,
    p_hours: hoursArray as unknown as string,
  });
  if (error) throw new Error(error.message);
}

export async function listBranchAppointments(
  branchId: string,
  from: string,
  to: string,
  resourceId?: string | null,
): Promise<Appointment[]> {
  const { data, error } = await supabase.rpc('list_branch_appointments', {
    p_branch_id: branchId,
    p_from: from,
    p_to: to,
    p_resource_id: resourceId ?? null,
  });

  if (error) throw new Error(error.message);
  return ((data ?? []) as AppointmentRow[]).map((row) => mapAppointment(row));
}

export async function listCustomerAppointments(
  customerId: string,
  limit = 50,
  offset = 0,
): Promise<Appointment[]> {
  const { data, error } = await supabase.rpc('list_customer_appointments', {
    p_customer_id: customerId,
    p_limit: limit,
    p_offset: offset,
  });

  if (error) throw new Error(error.message);
  return ((data ?? []) as AppointmentRow[]).map((row) => mapAppointment(row));
}

/**
 * Resumen liviano para el detalle del turno: visitas completadas y no-shows
 * del cliente (hasta 500 turnos — mismo criterio que la ficha de cliente).
 */
export async function getCustomerAppointmentStats(
  customerId: string,
): Promise<{ visits: number; noShows: number }> {
  const { data, error } = await supabase.rpc('list_customer_appointments', {
    p_customer_id: customerId,
    p_limit: 500,
    p_offset: 0,
  });
  if (error) throw new Error(error.message);
  let visits = 0;
  let noShows = 0;
  for (const row of (data ?? []) as { status?: string }[]) {
    if (row.status === 'completed') visits += 1;
    else if (row.status === 'no_show') noShows += 1;
  }
  return { visits, noShows };
}

export async function updateStatus(id: string, status: AppointmentStatus): Promise<void> {
  const { error } = await supabase.rpc('update_appointment_status', {
    p_appointment_id: id,
    p_status: status,
  });
  if (error) {
    throwAppointmentWriteFailure('appointments.write', new Error(error.message), id);
  }
  const staffEvent = resolveStaffNotifyAfterStatus(status);
  if (staffEvent) {
    void fireAppointmentStaffNotify(id, staffEvent);
  }
}

export async function cancelAppointment(id: string, reason?: string): Promise<void> {
  const { error } = await supabase.rpc('cancel_appointment', {
    p_appointment_id: id,
    p_reason: reason ?? null,
  });
  if (error) {
    throwAppointmentWriteFailure('appointments.write', new Error(error.message), id);
  }
  void fireAppointmentEmail(id, 'cancellation');
  void fireAppointmentStaffNotify(id, 'cancelled');
}

export async function createWalkInAppointment(input: {
  organizationId: string;
  branchId: string;
  resourceId: string;
  productId: string;
  /** PELU-07: if set, multi-service; productId should be first. */
  productIds?: string[] | null;
  customerId?: string | null;
  customerName: string;
  customerPhone?: string | null;
  customerEmail?: string | null;
  startsAt: string;
  endsAt: string;
  notes?: string | null;
  amountTotal?: number | null;
  quoteRequestId?: string | null;
  sessionIndex?: number | null;
  paymentMode?: 'none' | 'deposit' | 'full' | null;
  paymentStatus?: 'unpaid' | 'deposit_paid' | 'paid' | 'waived' | null;
  /** PELU-08: consumir una sesión del paquete al crear. */
  packageEntitlementId?: string | null;
}): Promise<Appointment> {
  const name = input.customerName.trim();
  if (!name) throw new Error('Nombre del cliente requerido');

  const paymentMode = input.paymentMode ?? 'none';
  // #755: default a 'unpaid' (no 'waived') — waived solo cuando se marca explícitamente
  const paymentStatus = input.paymentStatus ?? 'unpaid';
  const ids =
    input.productIds && input.productIds.length > 0
      ? input.productIds.filter(Boolean)
      : [input.productId];
  const primaryId = ids[0] ?? input.productId;

  const { data: rpcResult, error } = await supabase.rpc('create_appointment', {
    p_organization_id: input.organizationId,
    p_branch_id: input.branchId,
    p_resource_id: input.resourceId,
    p_product_id: primaryId,
    p_customer_name: name,
    p_customer_phone: normalizeOptionalArPhone(input.customerPhone),
    p_customer_email: input.customerEmail?.trim() || null,
    p_starts_at: input.startsAt,
    p_ends_at: input.endsAt,
    p_notes: input.notes?.trim() || null,
    p_customer_id: input.customerId?.trim() || null,
    p_amount_total: input.amountTotal ?? null,
    p_payment_mode: paymentMode,
    p_payment_status: paymentStatus,
    p_quote_request_id: input.quoteRequestId ?? null,
    p_session_index: input.sessionIndex ?? null,
    p_items: appointmentItemsToRpcPayload(ids),
    p_package_entitlement_id: input.packageEntitlementId ?? null,
  });

  if (error) {
    throwAppointmentWriteFailure(
      'appointments.write',
      new Error(appointmentWriteErrorMessage(error.message, 'No se pudo crear el turno.')),
      undefined,
      isAppointmentOverlapMessage(error.message),
    );
  }

  const appointmentId = (rpcResult as { appointment_id?: string })?.appointment_id;
  if (!appointmentId) {
    throwAppointmentWriteFailure(
      'appointments.write',
      new Error('No se pudo crear el turno.'),
    );
  }

  // Fetch the created appointment to return full row
  const { data: row, error: fetchError } = await supabase
    .from('appointments')
    .select(APPOINTMENT_SELECT)
    .eq('id', appointmentId)
    .single();

  if (fetchError || !row) {
    throwAppointmentWriteFailure(
      'appointments.write',
      new Error('No se pudo crear el turno.'),
      appointmentId,
    );
  }

  const appointment = await attachItems(mapAppointment(row as AppointmentRow));
  if (appointment.customerEmail) {
    void fireAppointmentEmail(appointment.id, 'confirmation');
  }
  void fireAppointmentStaffNotify(appointment.id, 'new', appointment.organizationId);
  return appointment;
}

export async function updateAppointment(input: {
  appointmentId: string;
  resourceId: string;
  productId: string;
  productIds?: string[] | null;
  customerId?: string | null;
  customerName: string;
  customerPhone?: string | null;
  customerEmail?: string | null;
  startsAt: string;
  endsAt: string;
  notes?: string | null;
  amountTotal?: number | null;
}): Promise<void> {
  const { data: beforeRow } = await supabase
    .from('appointments')
    .select('starts_at, resource_id, organization_id')
    .eq('id', input.appointmentId)
    .maybeSingle();

  const ids =
    input.productIds && input.productIds.length > 0
      ? input.productIds.filter(Boolean)
      : [input.productId];
  const primaryId = ids[0] ?? input.productId;

  const { error } = await supabase.rpc('update_appointment', {
    p_appointment_id: input.appointmentId,
    p_resource_id: input.resourceId,
    p_product_id: primaryId,
    p_customer_name: input.customerName,
    p_customer_phone: normalizeOptionalArPhone(input.customerPhone),
    p_customer_email: input.customerEmail?.trim() || null,
    p_starts_at: input.startsAt,
    p_ends_at: input.endsAt,
    p_notes: input.notes?.trim() || null,
    p_customer_id: input.customerId?.trim() || null,
    p_amount_total: input.amountTotal ?? null,
    p_items: appointmentItemsToRpcPayload(ids),
  });
  if (error) {
    throwAppointmentWriteFailure(
      'appointments.write',
      new Error(appointmentWriteErrorMessage(error.message, 'No se pudo guardar el turno.')),
      input.appointmentId,
      isAppointmentOverlapMessage(error.message),
    );
  }
  if (input.customerEmail?.trim()) {
    void fireAppointmentEmail(input.appointmentId, 'confirmation');
  }
  const staffEvent = resolveStaffNotifyAfterUpdate({
    before: beforeRow
      ? {
          startsAt: String((beforeRow as { starts_at: string }).starts_at),
          resourceId: String((beforeRow as { resource_id: string }).resource_id),
        }
      : null,
    after: { startsAt: input.startsAt, resourceId: input.resourceId },
  });
  if (staffEvent) {
    const orgId = (beforeRow as { organization_id?: string } | null)?.organization_id;
    void fireAppointmentStaffNotify(input.appointmentId, staffEvent, orgId);
  }
}

export async function listServiceProducts(
  organizationId: string,
  branchId?: string | null,
): Promise<ServiceProductOption[]> {
  // #757: si branchId está presente, filtrar solo servicios asignados a recursos de esa branch
  if (branchId) {
    const { data: assignedProductIds, error: assignedError } = await supabase
      .from('appointment_service_resources')
      .select('product_id')
      .in(
        'resource_id',
        (
          await supabase
            .from('appointment_resources')
            .select('id')
            .eq('branch_id', branchId)
            .eq('is_active', true)
        ).data?.map((r: { id: string }) => r.id) ?? [],
      );
    if (assignedError) throw new Error(assignedError.message);
    const productIds = ((assignedProductIds ?? []) as Array<{ product_id: string }>).map(
      (r) => r.product_id,
    );
    if (productIds.length === 0) return [];
    const { data, error } = await supabase
      .from('products')
      .select('id, name, duration_minutes, price')
      .eq('organization_id', organizationId)
      .eq('item_type', 'service')
      .eq('is_active', true)
      .is('deleted_at', null)
      .in('id', productIds)
      .order('name', { ascending: true });
    if (error) throw new Error(error.message);
    return ((data ?? []) as Array<{
      id: string;
      name: string;
      duration_minutes: number | null;
      price: number | null;
    }>)
      .filter((p) => p.duration_minutes != null && p.duration_minutes >= 5)
      .map((p) => ({
        id: p.id,
        name: p.name,
        durationMinutes: Number(p.duration_minutes),
        price: p.price != null ? Number(p.price) : 0,
      }));
  }

  const { data, error } = await supabase
    .from('products')
    .select('id, name, duration_minutes, price')
    .eq('organization_id', organizationId)
    .eq('item_type', 'service')
    .eq('is_active', true)
    .is('deleted_at', null)
    .order('name', { ascending: true });

  if (error) throw new Error(error.message);
  // ponytail: null duration = not bookable (same gate as portal / RPCs); no silent 60.
  return ((data ?? []) as Array<{
    id: string;
    name: string;
    duration_minutes: number | null;
    price: number | null;
  }>)
    .filter((p) => p.duration_minutes != null && p.duration_minutes >= 5)
    .map((p) => ({
      id: p.id,
      name: p.name,
      durationMinutes: p.duration_minutes as number,
      price: Number(p.price ?? 0),
    }));
}

export async function getBookingSettings(branchId: string): Promise<BookingSettings | null> {
  const { data, error } = await supabase
    .from('public_store_profiles')
    .select(
      'booking_enabled, booking_payment_mode, booking_deposit_amount, booking_deposit_percent, booking_cancel_hours, booking_min_lead_hours, booking_horizon_days, booking_unpaid_expire_minutes, booking_slot_interval_minutes, booking_waitlist_enabled, booking_waitlist_offer_minutes, booking_late_cancel_policy, booking_no_show_policy',
    )
    .eq('branch_id', branchId)
    .maybeSingle();

  if (error) throw new Error(error.message);
  if (!data) return null;

  const mode = data.booking_payment_mode;
  const interval = Number(data.booking_slot_interval_minutes ?? 15);
  return {
    bookingEnabled: data.booking_enabled === true,
    bookingPaymentMode:
      mode === 'deposit' || mode === 'full' || mode === 'none' ? mode : 'none',
    bookingDepositAmount:
      data.booking_deposit_amount != null ? Number(data.booking_deposit_amount) : null,
    bookingDepositPercent:
      data.booking_deposit_percent != null ? Number(data.booking_deposit_percent) : null,
    bookingCancelHours: Number(data.booking_cancel_hours ?? 2),
    bookingMinLeadHours: Number(data.booking_min_lead_hours ?? 0),
    bookingHorizonDays: Number(data.booking_horizon_days ?? 7),
    bookingUnpaidExpireMinutes: Number(data.booking_unpaid_expire_minutes ?? 30),
    // #756: aceptar cualquier intervalo válido (5, 10, 15, 20, 30, 60...) — no solo 15 o 30
    bookingSlotIntervalMinutes: interval > 0 ? interval : 15,
    bookingWaitlistEnabled: data.booking_waitlist_enabled === true,
    bookingWaitlistOfferMinutes: Number(data.booking_waitlist_offer_minutes ?? 15),
    bookingLateCancelPolicy:
      data.booking_late_cancel_policy === 'refund' ? 'refund' : 'keep',
    bookingNoShowPolicy:
      data.booking_no_show_policy === 'refund' ? 'refund' : 'keep',
  };
}

export async function updateBookingSettings(
  branchId: string,
  patch: Partial<BookingSettings>,
): Promise<void> {
  if (Object.keys(patch).length === 0) return;

  const { error } = await supabase.rpc('upsert_booking_settings', {
    p_branch_id: branchId,
    p_booking_enabled: patch.bookingEnabled ?? null,
    p_booking_payment_mode: patch.bookingPaymentMode ?? null,
    p_booking_deposit_amount: patch.bookingDepositAmount ?? null,
    p_booking_deposit_percent: patch.bookingDepositPercent ?? null,
    p_booking_cancel_hours: patch.bookingCancelHours ?? null,
    p_booking_min_lead_hours: patch.bookingMinLeadHours ?? null,
    p_booking_horizon_days: patch.bookingHorizonDays ?? null,
    p_booking_unpaid_expire_minutes: patch.bookingUnpaidExpireMinutes ?? null,
    p_booking_slot_interval_minutes: patch.bookingSlotIntervalMinutes ?? null,
    p_booking_waitlist_enabled: patch.bookingWaitlistEnabled ?? null,
    p_booking_waitlist_offer_minutes: patch.bookingWaitlistOfferMinutes ?? null,
    p_booking_late_cancel_policy: patch.bookingLateCancelPolicy ?? null,
    p_booking_no_show_policy: patch.bookingNoShowPolicy ?? null,
  });

  if (error) throw new Error(error.message);
}

/** Mon–Fri 09:00–18:00 (dow: Sun=0). */
export function defaultWeekdayHours(): ResourceHour[] {
  return [1, 2, 3, 4, 5].map((dayOfWeek) => ({
    dayOfWeek,
    startTime: '09:00',
    endTime: '18:00',
  }));
}

/** Map store profile business_hours → flat resource hours (open days only). */
export function businessHoursToResourceHours(days: BusinessHourDay[]): ResourceHour[] {
  const out: ResourceHour[] = [];
  for (const d of days) {
    if (!d.isOpen) continue;
    for (const s of d.shifts) {
      const startTime = String(s.open ?? '').slice(0, 5);
      const endTime = String(s.close ?? '').slice(0, 5);
      if (!startTime || !endTime || endTime <= startTime) continue;
      out.push({ dayOfWeek: d.dayOfWeek, startTime, endTime });
    }
  }
  return out;
}

/** Store hours if profile has open shifts; otherwise null. */
export async function fetchStoreResourceHours(branchId: string): Promise<ResourceHour[] | null> {
  const profile = await getCachedStoreProfile(branchId);
  if (!profile?.businessHours?.length) return null;
  const mapped = businessHoursToResourceHours(profile.businessHours);
  return mapped.length > 0 ? mapped : null;
}

/** Seed hours from store profile when it has open shifts; else default weekday. */
export async function resolveSeedHours(branchId: string): Promise<ResourceHour[]> {
  try {
    const fromStore = await fetchStoreResourceHours(branchId);
    if (fromStore) return fromStore;
  } catch {
    // ponytail: fall back to default if profile missing/unreachable
  }
  return defaultWeekdayHours();
}

const PORTAL_BASE = customerPortalBaseUrl();

/** Public booking link for the branch storefront; null if no public slug. */
export async function getBranchBookingUrl(branchId: string): Promise<string | null> {
  const profile = await getCachedStoreProfile(branchId);
  if (!profile?.slug || !profile.isPublic) return null;
  return storefrontPublicUrl(PORTAL_BASE, profile.slug);
}

type TimeOffRow = {
  id: string;
  resource_id: string;
  starts_at: string;
  ends_at: string;
  reason: string | null;
};

function mapTimeOff(row: TimeOffRow): ResourceTimeOff {
  return {
    id: row.id,
    resourceId: row.resource_id,
    startsAt: row.starts_at,
    endsAt: row.ends_at,
    reason: row.reason,
  };
}

export async function listResourceTimeOff(resourceId: string): Promise<ResourceTimeOff[]> {
  const { data, error } = await supabase
    .from('appointment_resource_time_off')
    .select('id, resource_id, starts_at, ends_at, reason')
    .eq('resource_id', resourceId)
    .order('starts_at', { ascending: true });

  if (error) throw new Error(error.message);
  return ((data ?? []) as TimeOffRow[]).map(mapTimeOff);
}

/** Time-off overlapping [fromIso, toIso) for all resources in a branch. */
export async function listBranchResourceTimeOff(
  branchId: string,
  fromIso: string,
  toIso: string,
): Promise<ResourceTimeOff[]> {
  const { data, error } = await supabase
    .from('appointment_resource_time_off')
    .select(
      'id, resource_id, starts_at, ends_at, reason, appointment_resources!inner(branch_id)',
    )
    .eq('appointment_resources.branch_id', branchId)
    .lt('starts_at', toIso)
    .gt('ends_at', fromIso)
    .order('starts_at', { ascending: true });

  if (error) throw new Error(error.message);
  return ((data ?? []) as TimeOffRow[]).map(mapTimeOff);
}

export async function createResourceTimeOff(input: {
  resourceId: string;
  startsAt: string;
  endsAt: string;
  reason?: string | null;
}): Promise<ResourceTimeOff> {
  if (input.endsAt <= input.startsAt) throw new Error('La fecha de fin debe ser posterior al inicio.');

  const { data, error } = await supabase
    .from('appointment_resource_time_off')
    .insert({
      resource_id: input.resourceId,
      starts_at: input.startsAt,
      ends_at: input.endsAt,
      reason: input.reason?.trim() || null,
    })
    .select('id, resource_id, starts_at, ends_at, reason')
    .single();

  if (error) throw new Error(error.message);
  return mapTimeOff(data as TimeOffRow);
}

export async function deleteResourceTimeOff(id: string): Promise<void> {
  const { error } = await supabase.from('appointment_resource_time_off').delete().eq('id', id);
  if (error) throw new Error(error.message);
}

export async function listResourceServiceIds(resourceId: string): Promise<string[]> {
  const { data, error } = await supabase
    .from('appointment_service_resources')
    .select('product_id')
    .eq('resource_id', resourceId);
  if (error) throw new Error(error.message);
  return ((data ?? []) as Array<{ product_id: string }>).map((r) => r.product_id);
}

/** Empty productIds = this resource offers all services (no restriction rows). */
export async function setResourceServices(resourceId: string, productIds: string[]): Promise<void> {
  const unique = [...new Set(productIds.map((id) => id.trim()).filter(Boolean))];
  const { error } = await supabase.rpc('set_resource_services', {
    p_resource_id: resourceId,
    p_product_ids: unique,
  });
  if (error) throw new Error(error.message);
}

/**
 * Resource → product ids it offers.
 * Missing key = unrestricted (offers all catalog services).
 */
export async function listResourceServiceMap(organizationId: string): Promise<Map<string, string[]>> {
  const { data: resources, error: rErr } = await supabase
    .from('appointment_resources')
    .select('id')
    .eq('organization_id', organizationId)
    .eq('is_active', true);
  if (rErr) throw new Error(rErr.message);
  const resourceIds = ((resources ?? []) as Array<{ id: string }>).map((r) => r.id);
  if (resourceIds.length === 0) return new Map();

  const { data, error } = await supabase
    .from('appointment_service_resources')
    .select('product_id, resource_id')
    .in('resource_id', resourceIds);
  if (error) throw new Error(error.message);

  const map = new Map<string, string[]>();
  for (const row of (data ?? []) as Array<{ product_id: string; resource_id: string }>) {
    const list = map.get(row.resource_id) ?? [];
    list.push(row.product_id);
    map.set(row.resource_id, list);
  }
  return map;
}

/**
 * Count of explicitly assigned services per resource.
 * Missing key = unrestricted (offers all catalog services).
 */
export async function listResourceServiceCounts(
  resourceIds: string[],
): Promise<Map<string, number>> {
  if (resourceIds.length === 0) return new Map();
  const { data, error } = await supabase
    .from('appointment_service_resources')
    .select('resource_id')
    .in('resource_id', resourceIds);
  if (error) throw new Error(error.message);
  const map = new Map<string, number>();
  for (const row of (data ?? []) as Array<{ resource_id: string }>) {
    map.set(row.resource_id, (map.get(row.resource_id) ?? 0) + 1);
  }
  return map;
}

export async function markAppointmentArrived(appointmentId: string): Promise<void> {
  const { error } = await supabase.rpc('mark_appointment_arrived', {
    p_appointment_id: appointmentId,
  });
  if (error) throw new Error(error.message);
}

export async function clearAppointmentArrived(appointmentId: string): Promise<void> {
  const { error } = await supabase.rpc('clear_appointment_arrived', {
    p_appointment_id: appointmentId,
  });
  if (error) throw new Error(error.message);
}

export async function completeAppointmentSale(
  appointmentId: string,
  saleId: string,
  amountPaid?: number | null,
): Promise<void> {
  const { error } = await supabase.rpc('complete_appointment_sale', {
    p_appointment_id: appointmentId,
    p_sale_id: saleId,
    p_amount_paid: amountPaid ?? null,
  });
  if (error) {
    throwAppointmentWriteFailure('appointments.write', new Error(error.message), appointmentId);
  }
}

export async function getAppointmentById(appointmentId: string): Promise<Appointment | null> {
  const { data, error } = await supabase
    .from('appointments')
    .select(APPOINTMENT_SELECT)
    .eq('id', appointmentId)
    .maybeSingle();
  if (error) throw new Error(error.message);
  if (!data) return null;
  return attachItems(mapAppointment(data as AppointmentRow));
}

/** Sessions linked to a quote plan (evaluation is via quote.evaluation_appointment_id, not this). */
export async function listAppointmentsByQuoteRequestId(
  quoteRequestId: string,
): Promise<Appointment[]> {
  const { data, error } = await supabase
    .from('appointments')
    .select(APPOINTMENT_SELECT)
    .eq('quote_request_id', quoteRequestId)
    .order('session_index', { ascending: true, nullsFirst: false })
    .order('starts_at', { ascending: true });
  if (error) throw new Error(error.message);
  return ((data ?? []) as AppointmentRow[]).map((row) => mapAppointment(row));
}

/** Light lookup for sheet/board labels — no full quote hydrate. */
export async function getQuoteEstimatedSessions(quoteRequestId: string): Promise<number | null> {
  const { data, error } = await supabase
    .from('service_quote_requests')
    .select('estimated_sessions')
    .eq('id', quoteRequestId)
    .maybeSingle();
  if (error) throw new Error(error.message);
  if (!data) return null;
  const n = Number((data as { estimated_sessions?: number }).estimated_sessions);
  return Number.isFinite(n) && n > 0 ? n : null;
}

/** Batch estimated_sessions for day-board labels — one query, unique ids. */
export type AgendaEmployeeReportRow = {
  employeeId: string | null;
  employeeName: string;
  resourceId: string;
  resourceName: string;
  requestedCount: number;
  confirmedCount: number;
  completedCount: number;
  noShowCount: number;
  cancelledCount: number;
  chargedCount: number;
  totalAmount: number;
  totalMinutes: number;
};

export async function reportAppointmentsByEmployee(
  branchId: string,
  from: string,
  to: string,
): Promise<AgendaEmployeeReportRow[]> {
  const { data, error } = await supabase.rpc('report_appointments_by_employee', {
    p_branch_id: branchId,
    p_from: from,
    p_to: to,
  });
  if (error) throw new Error(error.message);
  return ((data ?? []) as Array<{
    employee_id: string | null;
    employee_name: string;
    resource_id: string;
    resource_name: string;
    requested_count: number;
    confirmed_count: number;
    completed_count: number;
    no_show_count: number;
    cancelled_count: number;
    charged_count: number;
    total_amount: number;
    total_minutes: number;
  }>).map((row) => ({
    employeeId: row.employee_id,
    employeeName: row.employee_name,
    resourceId: row.resource_id,
    resourceName: row.resource_name,
    requestedCount: row.requested_count,
    confirmedCount: row.confirmed_count,
    completedCount: row.completed_count,
    noShowCount: row.no_show_count,
    cancelledCount: row.cancelled_count,
    chargedCount: row.charged_count,
    totalAmount: Number(row.total_amount),
    totalMinutes: Number(row.total_minutes),
  }));
}

export async function getQuoteEstimatedSessionsByIds(
  quoteRequestIds: string[],
): Promise<Map<string, number>> {
  const out = new Map<string, number>();
  const ids = [...new Set(quoteRequestIds.filter(Boolean))];
  if (ids.length === 0) return out;

  const { data, error } = await supabase
    .from('service_quote_requests')
    .select('id, estimated_sessions')
    .in('id', ids);
  if (error) throw new Error(error.message);

  for (const row of (data ?? []) as Array<{ id: string; estimated_sessions?: number }>) {
    const n = Number(row.estimated_sessions);
    if (Number.isFinite(n) && n > 0) out.set(row.id, n);
  }
  return out;
}

// ---------------------------------------------------------------------------
// PELU-09 negocio: cola de espera — lectura staff + oferta manual.
// RPCs SECURITY DEFINER: list = can_view Agenda, offer = can_edit Agenda.
// ---------------------------------------------------------------------------

type WaitlistRow = {
  id: string;
  product_id: string;
  resource_id: string | null;
  customer_user_id: string;
  customer_email: string | null;
  customer_name: string | null;
  preferred_from: string | null;
  preferred_to: string | null;
  status: AppointmentWaitlistEntry['status'];
  offered_resource_id: string | null;
  offered_starts_at: string | null;
  offered_ends_at: string | null;
  offer_expires_at: string | null;
  claimed_appointment_id: string | null;
  created_at: string;
};

export async function listBranchWaitlist(
  branchId: string,
): Promise<AppointmentWaitlistEntry[]> {
  const { data, error } = await supabase.rpc('list_branch_appointment_waitlist', {
    p_branch_id: branchId,
  });
  if (error) throw new Error(error.message);
  return ((data ?? []) as WaitlistRow[]).map((r) => ({
    id: r.id,
    productId: r.product_id,
    resourceId: r.resource_id,
    customerUserId: r.customer_user_id,
    customerEmail: r.customer_email,
    customerName: r.customer_name,
    preferredFrom: r.preferred_from,
    preferredTo: r.preferred_to,
    status: r.status,
    offeredResourceId: r.offered_resource_id,
    offeredStartsAt: r.offered_starts_at,
    offeredEndsAt: r.offered_ends_at,
    offerExpiresAt: r.offer_expires_at,
    claimedAppointmentId: r.claimed_appointment_id,
    createdAt: r.created_at,
  }));
}

export async function offerWaitlistEntry(input: {
  entryId: string;
  resourceId: string;
  startsAt: string;
  endsAt: string;
}): Promise<void> {
  const { error } = await supabase.rpc('offer_appointment_waitlist_entry', {
    p_entry_id: input.entryId,
    p_resource_id: input.resourceId,
    p_starts_at: input.startsAt,
    p_ends_at: input.endsAt,
  });
  if (error) {
    throwAppointmentWriteFailure(
      'appointments.waitlist',
      new Error(appointmentWriteErrorMessage(error.message, 'No se pudo ofrecer el turno.')),
      input.entryId,
      true,
    );
  }
}
